import { useState, useMemo } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import useCurrentUser from "../hooks/useCurrentUser";
import {
  MapPin,
  Package,
  Scale,
  Container,
  Battery,
  FileText,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Truck,
  Ship,
  Info
} from "lucide-react";

export default function CreateShipment() {
  const { clientId, role, loading } = useCurrentUser();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    origin: "",
    destination: "",
    cbm: "",
    weight: "",
    batteries: false,
    containerSize: "20GP",
    ctnQty: "",
    unitsQty: "",
    palletQty: "",
    notes: "",
  });

  const prices = {
    "20GP": 3000,
    "40HC": 6000,
  };

  const costs = useMemo(() => {
    const palletFee = (parseInt(form.palletQty) || 0) * 50;
    const cbmFee = (parseFloat(form.cbm) || 0) * 100;
    const weightFee = (parseFloat(form.weight) || 0) * 2.5;
    const batteryFee = form.batteries ? 100 : 0;
    const baseRate = prices[form.containerSize] || 0;

    return {
      baseRate,
      palletFee,
      cbmFee,
      weightFee,
      batteryFee,
      total: baseRate + palletFee + cbmFee + weightFee + batteryFee
    };
  }, [form]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!clientId || role !== "client") {
      alert("❌ Access denied. Not a valid client account.");
      return;
    }

    setIsSubmitting(true);

    try {
      const now = serverTimestamp();
      const timeline = [
        { label: "Pending Confirmation", timestamp: now },
        { label: "Confirmed", timestamp: null },
        { label: "Booked", timestamp: null },
        { label: "Arrived", timestamp: null },
      ];

      const shipmentData = {
        ...form,
        subtotal: costs.total, // Keep legacy field name for compatibility
        breakdown: costs,     // Store detailed breakdown
        status: "pending",
        createdAt: now,
        timeline,
        companyId: clientId, // Align with new seeder schema 
      };

      await addDoc(collection(db, "shipments"), shipmentData);

      alert("✅ Shipment request submitted and pending approval.");
      setForm({
        origin: "",
        destination: "",
        cbm: "",
        weight: "",
        batteries: false,
        containerSize: "20GP",
        ctnQty: "",
        unitsQty: "",
        palletQty: "",
        notes: "",
      });
    } catch (error) {
      console.error("❌ Error submitting shipment:", error);
      alert("Failed to submit shipment.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <div className="p-12 text-center text-slate-500">Loading shipment portal...</div>;
  if (!clientId || role !== "client") {
    return <div className="p-12 text-center text-red-500">Access denied: Only clients can submit shipments.</div>;
  }

  return (
    <div className="max-w-7xl mx-auto pb-12">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-navy-900 font-serif">Create Shipment</h1>
          <p className="text-slate-500 mt-1">Get an instant quote and book your cargo.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Form Column */}
        <div className="lg:col-span-2 space-y-6">
          <form id="shipment-form" onSubmit={handleSubmit} className="space-y-6">

            {/* Route Section */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 bg-slate-50 border-b border-slate-100 flex items-center gap-2">
                <MapPin className="text-navy-900" size={20} />
                <h2 className="font-bold text-navy-900">Route Details</h2>
              </div>
              <div className="p-6 grid md:grid-cols-2 gap-6">
                <Input
                  label="Origin Port / City"
                  name="origin"
                  value={form.origin}
                  onChange={handleChange}
                  placeholder="e.g. Shanghai, CN"
                  icon={<Ship size={16} className="text-slate-400" />}
                />
                <Input
                  label="Destination Details"
                  name="destination"
                  value={form.destination}
                  onChange={handleChange}
                  placeholder="e.g. Los Angeles, US"
                  icon={<MapPin size={16} className="text-slate-400" />}
                />
              </div>
            </div>

            {/* Cargo Section */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 bg-slate-50 border-b border-slate-100 flex items-center gap-2">
                <Package className="text-navy-900" size={20} />
                <h2 className="font-bold text-navy-900">Cargo & Container</h2>
              </div>
              <div className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-navy-900 mb-3">Container Type</label>
                  <div className="grid grid-cols-2 gap-4">
                    <div
                      onClick={() => setForm(f => ({ ...f, containerSize: "20GP" }))}
                      className={`cursor-pointer p-4 rounded-lg flex items-center justify-between border-2 transition-all ${form.containerSize === "20GP" ? "border-navy-900 bg-blue-50/50" : "border-slate-100 hover:border-slate-200"}`}
                    >
                      <div className="flex items-center gap-3">
                        <Container className={form.containerSize === "20GP" ? "text-navy-900" : "text-slate-400"} />
                        <div>
                          <div className="font-bold text-navy-900">20GP Standard</div>
                          <div className="text-xs text-slate-500">33 CBM Capacity</div>
                        </div>
                      </div>
                      {form.containerSize === "20GP" && <CheckCircle2 size={20} className="text-navy-900" />}
                    </div>

                    <div
                      onClick={() => setForm(f => ({ ...f, containerSize: "40HC" }))}
                      className={`cursor-pointer p-4 rounded-lg flex items-center justify-between border-2 transition-all ${form.containerSize === "40HC" ? "border-navy-900 bg-blue-50/50" : "border-slate-100 hover:border-slate-200"}`}
                    >
                      <div className="flex items-center gap-3">
                        <Container className={form.containerSize === "40HC" ? "text-navy-900" : "text-slate-400"} />
                        <div>
                          <div className="font-bold text-navy-900">40HC High Cube</div>
                          <div className="text-xs text-slate-500">76 CBM Capacity</div>
                        </div>
                      </div>
                      {form.containerSize === "40HC" && <CheckCircle2 size={20} className="text-navy-900" />}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Input label="Total CBM" name="cbm" type="number" value={form.cbm} onChange={handleChange} suffix="m³" />
                  <Input label="Total Weight" name="weight" type="number" value={form.weight} onChange={handleChange} suffix="kg" />
                  <Input label="Pallets" name="palletQty" type="number" value={form.palletQty} onChange={handleChange} />
                  <Input label="Cartons" name="ctnQty" type="number" value={form.ctnQty} onChange={handleChange} />
                </div>
              </div>
            </div>

            {/* Additional Details */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 bg-slate-50 border-b border-slate-100 flex items-center gap-2">
                <FileText className="text-navy-900" size={20} />
                <h2 className="font-bold text-navy-900">Additional Details</h2>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-100">
                  <div className="mt-1"><Battery className="text-amber-600" size={20} /></div>
                  <div className="flex-1">
                    <div className="font-semibold text-navy-900">Dangerous Goods Definition</div>
                    <p className="text-xs text-slate-600 mt-1">Does this shipment contain lithium batteries or other regulated materials?</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" name="batteries" checked={form.batteries} onChange={handleChange} className="sr-only peer" />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-navy-900 mb-2">Special Instructions</label>
                  <textarea
                    name="notes"
                    rows={3}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-900 transition-all placeholder:text-slate-400"
                    placeholder="E.g., Gate code required, notify 24h before delivery..."
                    value={form.notes}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Live Quote Column */}
        <div className="lg:col-span-1">
          <div className="sticky top-6 space-y-6">
            <div className="bg-navy-900 rounded-xl shadow-lg shadow-navy-900/20 text-white overflow-hidden">
              <div className="p-6 border-b border-white/10">
                <h2 className="text-lg font-bold font-serif">Estimated Quote</h2>
                <p className="text-navy-200 text-xs mt-1">Real-time calculation based on cargo</p>
              </div>

              <div className="p-6 space-y-4">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-navy-100">Base Freight ({form.containerSize})</span>
                  <span className="font-medium">{formatUSD(costs.baseRate)}</span>
                </div>

                {costs.palletFee > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-navy-100">Pallet Surcharge ({form.palletQty})</span>
                    <span className="font-medium">{formatUSD(costs.palletFee)}</span>
                  </div>
                )}

                {costs.cbmFee > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-navy-100">Volume Charge</span>
                    <span className="font-medium">{formatUSD(costs.cbmFee)}</span>
                  </div>
                )}

                {costs.weightFee > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-navy-100">Weight Charge</span>
                    <span className="font-medium">{formatUSD(costs.weightFee)}</span>
                  </div>
                )}

                {costs.batteryFee > 0 && (
                  <div className="flex justify-between items-center text-sm text-amber-400">
                    <span className="flex items-center gap-1"><AlertCircle size={12} /> DG Surcharge</span>
                    <span className="font-medium">{formatUSD(costs.batteryFee)}</span>
                  </div>
                )}

                <div className="h-px bg-white/10 my-2"></div>

                <div className="flex justify-between items-end">
                  <span className="text-sm font-semibold text-white">Total Estimate</span>
                  <span className="text-3xl font-bold text-white">{formatUSD(costs.total)}</span>
                </div>
              </div>

              <div className="p-4 bg-navy-800">
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="w-full bg-gold-500 hover:bg-gold-400 text-navy-950 font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Processing..." : "Submit Booking Request"}
                  {!isSubmitting && <ArrowRight size={18} />}
                </button>
                <p className="text-[10px] text-navy-300 text-center mt-3">
                  *Final invoice may vary based on actual weight/dimensions upon receipt.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
              <h3 className="font-bold text-navy-900 mb-3 flex items-center gap-2">
                <Info size={16} className="text-blue-500" /> Need Help?
              </h3>
              <p className="text-sm text-slate-500 mb-4 leading-relaxed">
                Unsure about container sizes or incoterms? Our support team is available 24/7 to assist with your booking.
              </p>
              <button className="text-sm font-semibold text-navy-900 border border-slate-300 rounded-lg w-full py-2 hover:bg-slate-50 transition-colors">
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Input({ label, name, value, type = "text", onChange, placeholder, icon, suffix }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-navy-900 mb-1.5">{label}</label>
      <div className="relative">
        {icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
            {icon}
          </div>
        )}
        <input
          name={name}
          type={type}
          className={`w-full bg-slate-50 border border-slate-200 rounded-lg py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-900 transition-all placeholder:text-slate-400 ${icon ? 'pl-10' : 'pl-4'} ${suffix ? 'pr-10' : 'pr-4'}`}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
        />
        {suffix && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 text-xs font-semibold">
            {suffix}
          </div>
        )}
      </div>
    </div>
  );
}

function formatUSD(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value || 0);
}